import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { ProposalFieldLabelCell, TextCell, AdjustmentCell, PercentageAdjustmentCell } from '../../../../views/components/cells';
import { getRiskProfileRuleData, formatRiskProfileTargetMinMax, formatMaxTarget } from '../../../../utils/risk-profiles';
import { RiskProfileRuleData, RiskProfileRule, RiskProfileRuleType } from '../../../../types/risk-profiles';
import Nullable from '@/types/nullable';
import DonutData from '@/types/donut-data';
import ProposalTableData from '@/types/proposal-table-data';
import ProposalTableCellData from '@/types/proposal-table-cell-data';
import ProposalTableRowData from '@/types/proposal-table-row-data';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import RiskProfileItem from '@/types/risk-profile-item';
import { ComparisonItemDAO } from '../ProposalItemDAO';

/**
 * @class Risk Profile Data Access Object
 */
export default class RiskProfileDAO extends ComparisonItemBasedProposalDAO {
  ruleData: RiskProfileRuleData[] = [];

  rule: Nullable<RiskProfileRule> = null;

  showTarget: boolean = true;

  summaryView: boolean = false;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new RiskProfileDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['ASSET_ALLOCATION_COLORS', 'RISK_ALLOCATION_COLORS', 'RISK_LEVELS'];
  }

  buildSummary(riskProfile: RiskProfileItem, itemDAO: ComparisonItemDAO, index: number) {
    for (const rule of riskProfile.rules) {
      if (rule.ruleType === RiskProfileRuleType.AllowedRisk) {
        const ruleData = getRiskProfileRuleData(rule, itemDAO.item, this.locale, this.proposal.appUser);

        const field = new ProposalFieldDAO('', {
          adjustment: ruleData[0].adjustment,
          value: ruleData[0].value,
        });
        field.cellType = PercentageAdjustmentCell;
        field.decimalPlace = 0;
        this.addFieldDAO(itemDAO, field);

        if (index < 1) {
          this.addFieldName(this.formatMessage(`RiskRating.${rule.value.riskLevel}`), ProposalStyleClassNames.ProposalTableContent);
        }
      }
    }
  }

  protected build(): void {
    // test for options
    const daoConfig: ProposalNS.RiskProfileConfig = this.proposalElement.DAO as ProposalNS.RiskProfileConfig;

    if (daoConfig.showTarget != null) {
      this.showTarget = daoConfig.showTarget;
    }

    if (daoConfig.summaryView != null) {
      this.summaryView = daoConfig.summaryView;
    }

    if (this.summaryView) {
      const { riskProfile } = this.proposal;
      if (riskProfile == null) {
        const itemDAO = this.nextItem();
        if (itemDAO !== null && itemDAO.item.riskProfile != null) {
          this.buildSummary(itemDAO.item.riskProfile, itemDAO, 0);
        }
      } else {
        let index = 0;
        let itemDAO = this.nextItem();
        while (itemDAO !== null) {
          this.buildSummary(riskProfile, itemDAO, index);
          itemDAO = this.nextItem();
          index++;
        }
      }
    } else {
      this.addFieldName('Target', ProposalStyleClassNames.ProposalTableContent);
      this.addFieldName('Actual', ProposalStyleClassNames.ProposalTableContent);

      let { riskProfile } = this.proposal;
      const filter = daoConfig.filterItemsByIndexDAOOptions;

      if (!riskProfile && filter && filter.selection && filter.selection.length > 0 && this.proposal.items[filter.selection[0]]) {
        riskProfile = this.proposal.items[filter.selection[0]].riskProfile;
      }

      if (!riskProfile || !riskProfile.rules[daoConfig.ruleIndex]) {
        return;
      }

      let itemDAO = this.nextItem();
      while (itemDAO !== null) {
        this.rule = riskProfile.rules[daoConfig.ruleIndex];
        this.ruleData = this.ruleData.concat(getRiskProfileRuleData(this.rule!, itemDAO.item, this.locale, this.proposal.appUser));

        itemDAO = this.nextItem();
      }
    }
  }

  getDonutData(): DonutData | null {
    const out: DonutData | null = {
      data: [],
      colorsDict: null,
    };

    out.data.push(
      this.ruleData.map(data => ({
        name: data.key,
        y: data.value || 0,
      })),
    );

    if (this.showTarget) {
      out.data.push(
        this.ruleData.map(data => ({
          name: data.key,
          y: ((data.target.max || 100) - (data.target.min || 0)) / 2,
        })),
      );
    }

    if (this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }

  getTableData(): ProposalTableData {
    if (this.summaryView) {
      const data = super.getTableData();
      let { riskProfile } = this.proposal;
      if (riskProfile == null) {
        this.resetItemIndex();
        const itemDAO = this.nextItem();
        if (itemDAO !== null && itemDAO.item.riskProfile != null) {
          riskProfile = itemDAO.item.riskProfile;
        }
      }

      if (data.header != null && riskProfile != null) {
        // header
        data.header.data.splice(1, 0, {
          value: 'Target',
          className: 'item-name',
        });

        // rows
        for (let index = 0; index < data.rows.length; index++) {
          const row = data.rows[index];
          const rule = riskProfile.rules[index];
          const value = rule.ruleType === RiskProfileRuleType.AllowedRisk ? rule.value.max : null;
          row.data.splice(1, 0, {
            value: value != null ? formatMaxTarget(value, 0) : value,
            cellType: TextCell,
            decimalPlace: 0,
          });
        }

        return data;
      }
    }

    if (!this.rule) {
      return { rows: [] };
    }

    const emptyCell = {
      value: '',
      cellType: ProposalFieldLabelCell,
    };

    const filteredData = this.ruleData.filter(d => !d.hidden);
    const headers: ProposalTableCellData[] = [emptyCell];

    for (const data of filteredData) {
      headers.push({
        value: data.label || '',
        cellType: ProposalFieldLabelCell,
        color: this.colorsDict ? this.colorsDict[data.key] : undefined,
      });
    }

    headers.push(emptyCell);

    const adjustmentCells = filteredData.map(data => ({
      value: { adjustment: data.adjustment || 0 },
      cellType: AdjustmentCell,
      decimalPlace: 1,
    }));

    const rows: ProposalTableRowData[] = [
      {
        data: [
          {
            value: this.formatMessage('Common.Target'),
            cellType: ProposalFieldLabelCell,
          },
          ...filteredData.map(data => ({
            value: formatRiskProfileTargetMinMax(data.target.min, data.target.max, this.locale),
            cellType: TextCell,
          })),
          {
            value: '',
            cellType: TextCell,
          },
        ],
      },
      {
        data: [
          {
            value: this.formatMessage('Common.Actual'),
            cellType: ProposalFieldLabelCell,
          },
          ...filteredData.map(data => ({
            value: data.value != null ? this.formatPercentage(data.value, 1) : '',
            cellType: TextCell,
          })),
          ...(adjustmentCells.length <= 1 ? adjustmentCells : []),
        ],
      },
    ];

    if (adjustmentCells.length > 1) {
      rows.push({
        data: [emptyCell, ...adjustmentCells],
      });
    }

    const otherRule = this.ruleData.find(data => data.key === 'RISK_OTHER');
    const otherAlloc = otherRule ? otherRule.value : null;
    if (otherAlloc != null && otherAlloc > 0) {
      rows.push({
        className: 'data-unavailable-row',
        data: [
          {
            colSpan: 4,
            value: `${this.formatMessage('Common.DataUnavailable')} (${this.formatPercentage(otherAlloc, 1)})`,
            cellType: ProposalFieldLabelCell,
            color: this.colorsDict ? this.colorsDict.RISK_OTHER : undefined,
          },
        ],
      });
    }

    return {
      header: {
        data: headers,
      },
      rows,
    };
  }
}
